import {
    Box,
    Button,
    Chip,
    IconButton,
    Link,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";

import { monospaceStyle } from "../../theme";
import { ChainCard, CoverageRow } from "../../types/valueChain";
import {
    ExportColumn,
    ExportTable,
    tableExportOptions,
} from "../../utils/export/table";
import ExportMenu from "../common/ExportMenu";
import { countryName, flagWords, isRuleFlag } from "./chainLens";
import {
    FlagChip,
    SingleMakerChip,
    TRUSTED_COLOUR,
    domesticColour,
} from "./LensBlocks";
import { ChainDetailRef } from "./valueChainState";

// The columns of the report a reader may sort by
export type CoverageColumn =
    | "kind"
    | "products"
    | "makers"
    | "domestic"
    | "trusted"
    | "other"
    | "unknown"
    | "proven"
    | "articles"
    | "flag";

export type SortOrder = "asc" | "desc";

export interface CoverageSort {
    column: CoverageColumn;
    order: SortOrder;
}

// The report opens on the kinds with the fewest domestic makers first
export const DEFAULT_COVERAGE_SORT: CoverageSort = {
    column: "domestic",
    order: "asc",
};

// Rows on one page of the report
export const COVERAGE_PAGE_SIZE = 10;

type NumberColumn = Exclude<CoverageColumn, "kind" | "flag">;

const NUMBER_COLUMNS: { column: NumberColumn; title: string; words: string }[] = [
    { column: "products", title: "Products", words: "products" },
    { column: "makers", title: "Makers", words: "makers" },
    { column: "domestic", title: "Domestic", words: "domestic" },
    { column: "trusted", title: "Trusted", words: "trusted" },
    { column: "other", title: "Other country", words: "in another country" },
    { column: "unknown", title: "Unknown", words: "unknown" },
    { column: "proven", title: "Proven", words: "proven" },
    { column: "articles", title: "Articles", words: "articles" },
];

// What a sort is called beside the report's title: "fewest domestic"
export const sortWords = (sort: CoverageSort): string => {
    if (sort.column === "kind") {
        return sort.order === "asc" ? "kind, A to Z" : "kind, Z to A";
    }
    const words =
        sort.column === "flag"
            ? "flags"
            : (NUMBER_COLUMNS.find((entry) => entry.column === sort.column)
                  ?.words ?? sort.column);
    return `${sort.order === "asc" ? "fewest" : "most"} ${words}`;
};

// The name a row goes by: its label, else its card's id
export const rowName = (row: CoverageRow): string => row.label ?? row.card;

const sortValue = (
    row: CoverageRow,
    column: CoverageColumn,
): number | string => {
    switch (column) {
        case "kind":
            return rowName(row).toLowerCase();
        case "flag":
            return row.flags.length;
        default:
            return row[column];
    }
};

// The rows in the order of a sort. Rows equal by it keep the response's
// order (the root first, then the cards by column).
export const sortCoverage = (
    rows: readonly CoverageRow[],
    sort: CoverageSort,
): CoverageRow[] => {
    const sign = sort.order === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
        const left = sortValue(a, sort.column);
        const right = sortValue(b, sort.column);
        if (left < right) {
            return -sign;
        }
        if (left > right) {
            return sign;
        }
        return 0;
    });
};

const EXPORT_COLUMNS: ExportColumn[] = [
    { header: "kind", width: 36 },
    { header: "card", width: 40 },
    { header: "type" },
    { header: "side" },
    { header: "step", kind: "number" },
    { header: "unplaced" },
    { header: "products", kind: "number" },
    { header: "makers", kind: "number" },
    { header: "domestic", kind: "number" },
    { header: "trusted", kind: "number" },
    { header: "other", kind: "number" },
    { header: "unknown", kind: "number" },
    { header: "proven", kind: "number" },
    { header: "articles", kind: "number" },
    { header: "flag", width: 40 },
];

// The side a row is on in the export: its side, "root" for the chain's
// root, "opened" for an opened card that only its own read has
const csvSide = (row: CoverageRow, rootCard: string | null): string =>
    row.side ?? (rootCard === null || row.card === rootCard ? "root" : "opened");

// The report's rows as an export table, in the order given: the response's
// numbers, never recounted
export const coverageTable = (
    rows: readonly CoverageRow[],
    rootCard: string | null = null,
): ExportTable => ({
    name: "Coverage",
    columns: EXPORT_COLUMNS,
    rows: rows.map((row) => [
        rowName(row),
        row.card,
        row.type,
        csvSide(row, rootCard),
        row.step,
        String(row.unplaced),
        row.products,
        row.makers,
        row.domestic,
        row.trusted,
        row.other,
        row.unknown,
        row.proven,
        row.articles,
        row.flags.map((flag) => flagWords(flag, row.single_maker_country)),
    ]),
});

interface CoverageReportProps {
    // The coverage rows of every read the page holds: the root read's first,
    // then those of the opened cards' reads, each card once
    rows: readonly CoverageRow[];
    // The id of the chain's root, for the export's file name
    rootId: string;
    // The name of the chain's root, for the title
    rootName?: string | null;
    // The id of the root's card, as the read names it; the one row marked
    // as the root
    rootCard?: string | null;
    // The home jurisdiction the rows are split by
    home: string | null;
    // Whether a trusted set is named: the trusted column is shown then
    trusted?: boolean;
    // The cards of every read the page holds, by id: a row's level is its
    // card's nature, its name opens the card's kind
    cards?: ReadonlyMap<string, ChainCard>;
    // Opens the detail of a row's kind
    onOpenDetail?: (detail: ChainDetailRef) => void;
}

// The pager under the report: the previous page, each page, the next one
const Pager: React.FC<{
    page: number;
    pages: number;
    onPage: (page: number) => void;
}> = ({ page, pages, onPage }) => (
    <Box
        component="nav"
        aria-label="Report pages"
        sx={{ display: "flex", alignItems: "center", gap: 0.25 }}
    >
        <IconButton
            size="small"
            aria-label="Go to previous page"
            disabled={page === 0}
            onClick={() => onPage(page - 1)}
            sx={{ fontSize: 14, width: 24, height: 24 }}
        >
            ‹
        </IconButton>
        {Array.from({ length: pages }, (_unused, index) => (
            <Button
                key={index}
                size="small"
                aria-label={`Page ${index + 1}`}
                aria-current={index === page ? "page" : undefined}
                onClick={() => onPage(index)}
                sx={{
                    ...monospaceStyle,
                    minWidth: 24,
                    height: 24,
                    px: 0.5,
                    fontSize: 12,
                    color: index === page ? "text.primary" : "text.secondary",
                }}
            >
                {index + 1}
            </Button>
        ))}
        <IconButton
            size="small"
            aria-label="Go to next page"
            disabled={page >= pages - 1}
            onClick={() => onPage(page + 1)}
            sx={{ fontSize: 14, width: 24, height: 24 }}
        >
            ›
        </IconButton>
    </Box>
);

// The sorts the caption beside the title offers
const SORT_OPTIONS: CoverageSort[] = [
    { column: "domestic", order: "asc" },
    { column: "makers", order: "asc" },
    { column: "proven", order: "asc" },
    { column: "products", order: "desc" },
    { column: "articles", order: "desc" },
    { column: "flag", order: "desc" },
    { column: "kind", order: "asc" },
];

/**
 * The coverage report under the chain: every kind of the chain in one table
 * with its level, products, makers by where they are at home, proven makers,
 * articles and flag. Sorted by fewest domestic first, paged, exportable.
 * Every number is the response's; the page never recounts.
 */
const CoverageReport: React.FC<CoverageReportProps> = ({
    rows,
    rootId,
    rootName = null,
    rootCard = null,
    home,
    trusted = false,
    cards = new Map(),
    onOpenDetail,
}) => {
    const [sort, setSort] = useState<CoverageSort>(DEFAULT_COVERAGE_SORT);
    const [page, setPage] = useState(0);
    const [sortAnchor, setSortAnchor] = useState<HTMLElement | null>(null);

    const columns = NUMBER_COLUMNS.filter(
        ({ column }) => trusted || column !== "trusted",
    );
    const sorted = useMemo(() => sortCoverage(rows, sort), [rows, sort]);
    const pages = Math.max(1, Math.ceil(sorted.length / COVERAGE_PAGE_SIZE));
    const shownPage = Math.min(page, pages - 1);
    const first = shownPage * COVERAGE_PAGE_SIZE;
    const pageRows = sorted.slice(first, first + COVERAGE_PAGE_SIZE);

    const exportOptions = tableExportOptions(() => ({
        tables: [coverageTable(sorted, rootCard)],
        meta: {
            title: `Supply coverage: ${rootName ?? rootId}`,
            generatedAt: new Date(),
            filters: [
                ["Root", rootName ?? rootId],
                ["Home jurisdiction", home ? countryName(home) : "none"],
                ["Order", sortWords(sort)],
            ],
            note: "Every kind of the opened chain, all pages, in the order shown.",
        },
        filename: ["coverage", home ?? "none", rootId],
    }));

    const sortBy = (column: CoverageColumn) => {
        setSort((current) =>
            current.column === column
                ? { column, order: current.order === "asc" ? "desc" : "asc" }
                : { column, order: "asc" },
        );
        setPage(0);
    };

    const head = (column: CoverageColumn, title: string, numeric: boolean) => (
        <TableCell
            key={column}
            align={numeric ? "right" : "left"}
            sortDirection={sort.column === column ? sort.order : false}
        >
            <TableSortLabel
                active={sort.column === column}
                direction={sort.column === column ? sort.order : "asc"}
                onClick={() => sortBy(column)}
            >
                {title}
            </TableSortLabel>
        </TableCell>
    );

    return (
        <Box
            component="section"
            aria-label="Coverage report"
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                p: "16px 18px 14px",
                border: 1,
                borderColor: "divider",
                borderRadius: (theme) => `${theme.radius.surface}px`,
                backgroundColor: "background.paper",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                }}
            >
                <Typography variant="h6" component="h2" sx={{ m: 0 }}>
                    Coverage in this chain
                    <Box
                        component="span"
                        sx={{ color: "text.secondary", fontWeight: 400 }}
                    >
                        {" "}
                        · every kind under {rootName ?? rootId}
                        {home ? `, home ${countryName(home)}` : ""}
                    </Box>
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Link
                        component="button"
                        type="button"
                        underline="hover"
                        aria-haspopup="menu"
                        onClick={(event: React.MouseEvent<HTMLElement>) =>
                            setSortAnchor(event.currentTarget)
                        }
                        sx={{ fontSize: 12, color: "text.secondary" }}
                    >
                        sorted by {sortWords(sort)} ▾
                    </Link>
                    <Menu
                        anchorEl={sortAnchor}
                        open={sortAnchor !== null}
                        onClose={() => setSortAnchor(null)}
                    >
                        {SORT_OPTIONS.map((option) => (
                            <MenuItem
                                key={option.column}
                                dense
                                selected={
                                    option.column === sort.column &&
                                    option.order === sort.order
                                }
                                onClick={() => {
                                    setSort(option);
                                    setPage(0);
                                    setSortAnchor(null);
                                }}
                            >
                                {sortWords(option)}
                            </MenuItem>
                        ))}
                    </Menu>
                    <Box
                        component="span"
                        title={`All ${sorted.length} rows, every page, in the order shown`}
                        sx={{ fontSize: 12 }}
                    >
                        <ExportMenu
                            options={exportOptions}
                            disabled={sorted.length === 0}
                        />
                    </Box>
                </Box>
            </Box>

            <Box sx={{ overflowX: "auto" }}>
                <Table size="small" aria-label="Coverage">
                    <TableHead>
                        <TableRow>
                            {head("kind", "Kind", false)}
                            <TableCell>Level</TableCell>
                            {columns.map(({ column, title }) =>
                                head(column, title, true),
                            )}
                            {head("flag", "Flag", false)}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pageRows.map((row) => {
                            const card = cards.get(row.card);
                            return (
                                <TableRow
                                    key={row.card}
                                    data-flagged={row.flags.length > 0}
                                >
                                    <TableCell>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            {card && onOpenDetail ? (
                                                <Link
                                                    component="button"
                                                    type="button"
                                                    underline="hover"
                                                    variant="body2"
                                                    onClick={() =>
                                                        onOpenDetail({
                                                            type: "category",
                                                            id: card.category_id,
                                                        })
                                                    }
                                                    sx={{ textAlign: "left" }}
                                                >
                                                    {rowName(row)}
                                                </Link>
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    component="span"
                                                >
                                                    {rowName(row)}
                                                </Typography>
                                            )}
                                            {row.side === null &&
                                                (rootCard === null ||
                                                    row.card === rootCard) && (
                                                    <Chip
                                                        label="root"
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            height: 18,
                                                            fontSize: "0.65rem",
                                                        }}
                                                    />
                                                )}
                                            {row.unplaced && (
                                                <Chip
                                                    label="unplaced class"
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        height: 18,
                                                        fontSize: "0.65rem",
                                                        color: "text.secondary",
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ color: "text.secondary" }}>
                                        {card?.nature ?? "—"}
                                    </TableCell>
                                    {columns.map(({ column }) => (
                                        <TableCell
                                            key={column}
                                            align="right"
                                            sx={{
                                                ...monospaceStyle,
                                                ...(column === "domestic"
                                                    ? {
                                                          color: domesticColour(
                                                              row.domestic,
                                                          ),
                                                          fontWeight: 500,
                                                      }
                                                    : column === "trusted"
                                                      ? {
                                                            color: TRUSTED_COLOUR,
                                                            fontWeight: 500,
                                                        }
                                                      : column === "articles"
                                                        ? {
                                                              color: "text.secondary",
                                                          }
                                                        : {}),
                                            }}
                                        >
                                            {row[column]}
                                        </TableCell>
                                    ))}
                                    <TableCell>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 0.5,
                                            }}
                                        >
                                            {row.flags.map((flag) =>
                                                isRuleFlag(flag) ? (
                                                    <FlagChip
                                                        key={flag}
                                                        flag={flag}
                                                    />
                                                ) : (
                                                    <SingleMakerChip
                                                        key={flag}
                                                        country={
                                                            row.single_maker_country
                                                        }
                                                    />
                                                ),
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                }}
            >
                <Typography
                    variant="caption"
                    color="text.secondary"
                    aria-label="Rows shown"
                >
                    <Box component="span" sx={monospaceStyle}>
                        {sorted.length === 0 ? 0 : first + 1}–
                        {first + pageRows.length}
                    </Box>{" "}
                    of{" "}
                    <Box component="span" sx={monospaceStyle}>
                        {sorted.length}
                    </Box>{" "}
                    {sorted.length === 1 ? "kind" : "kinds"} · the same counts
                    as the cards, every kind of the opened chain in one table
                </Typography>
                <Pager page={shownPage} pages={pages} onPage={setPage} />
            </Box>
        </Box>
    );
};

export default CoverageReport;
